import { useState } from "react"
import { Box, ButtonGroup, IconButton, Pagination } from "@chakra-ui/react"
import { HiChevronLeft, HiChevronRight } from "react-icons/hi"
import Figure, { type PopOverProps } from "./Figure"

export interface PaginatedFigureCardProps<T> {
  /** Items to page through, one per "page". */
  items: T[]
  /** Renders the body for a single item. Receives the item and its index. */
  renderItem: (item: T, index: number) => React.ReactNode
  /** Card heading (passed straight to Figure). */
  heading?: string
  loading?: boolean
  /** Shorter card for dense strips (system overview headlines). */
  compact?: boolean
  /** Rendered in place of an item when `items` is empty and not loading. */
  emptyState?: React.ReactNode
  /**
   * Info popover for the card. Receives the item currently on screen, so the panel can describe
   * that item rather than the collection — return undefined for items that need no popover.
   */
  renderPopover?: (item: T, index: number) => PopOverProps | undefined
}

/**
 * Generic "flip between items in a single card" figure.
 *
 * Extracted from the flip mechanism in `CurrentOrderStatus` so it can be reused
 * for any card that cycles through multiple items in place (e.g. the Burnaston
 * "Capacity to Fill" card, which flips between vehicle types). The chevron
 * controls only appear when there is more than one item.
 *
 * This component owns ONLY the pagination state — per the shared-component rules
 * it stays dumb about what each item is; callers supply `renderItem`.
 *
 * NOTE: `CurrentOrderStatus` predates this and still has its own inline copy of
 * the same pattern. It could be refactored onto this component as a follow-up,
 * but that touches a live card with bespoke layout/truncation, so it is left
 * untouched here to avoid an unverifiable visual regression.
 */
function PaginatedFigureCard<T>({
  items,
  renderItem,
  heading,
  loading,
  compact,
  emptyState,
  renderPopover,
}: PaginatedFigureCardProps<T>) {
  const [currentPage, setCurrentPage] = useState(1)

  const count = items.length
  const hasMultiple = count > 1
  // Clamp against the current item count so a shrinking list can't point past the end.
  const page = count > 0 ? Math.min(currentPage, count) : 1
  const currentItem = count > 0 ? items[page - 1] : null

  const pagination = hasMultiple ? (
    <Pagination.Root
      count={count}
      pageSize={1}
      page={page}
      onPageChange={(e) => setCurrentPage(e.page)}
    >
      {/* 2xs + no gap: keeps the control the same visual weight as the 13px heading it sits beside. */}
      <ButtonGroup variant="ghost" size="2xs" gap="0" flexShrink={0} alignItems="center">
        <Pagination.PrevTrigger asChild>
          <IconButton size="2xs" aria-label="Previous item">
            <HiChevronLeft />
          </IconButton>
        </Pagination.PrevTrigger>
        <Pagination.PageText whiteSpace="nowrap" fontSize="11px" fontWeight="500" color="#8A8A8A" />
        <Pagination.NextTrigger asChild>
          <IconButton size="2xs" aria-label="Next item">
            <HiChevronRight />
          </IconButton>
        </Pagination.NextTrigger>
      </ButtonGroup>
    </Pagination.Root>
  ) : undefined

  return (
    <Figure
      heading={heading}
      loading={loading}
      compact={compact}
      headerRight={pagination}
      popover={currentItem != null ? renderPopover?.(currentItem, page - 1) : undefined}
    >
      {currentItem == null ? (
        emptyState ?? null
      ) : (
        <Box width="100%" flex="1" minWidth={0}>
          {renderItem(currentItem, page - 1)}
        </Box>
      )}
    </Figure>
  )
}

export default PaginatedFigureCard
